/**
 * A small, self-contained QR Code generator — enough to render the enrolment
 * `otpauth://` URL client-side, so the shared TOTP secret never leaves the
 * browser as an image request to a third party (G17, ADR-0030).
 *
 * Byte mode, error-correction level M, versions 1–10 (capacity 213 bytes at
 * v10-M — otpauth URLs are ~100–150 chars, so this always fits). The
 * algorithm follows the QR Code specification (ISO/IEC 18004); the structure
 * mirrors Nayuki's reference generator (MIT). The ECC block tables below are
 * cross-checked against the standard's per-version data capacities.
 */

// Level-M error-correction codewords per block and blocks per version.
// Index 0 is unused padding. Verified: total codewords − ecc = data capacity.
const ECC_CODEWORDS_PER_BLOCK = [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26];
const NUM_BLOCKS = [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5];
const MAX_VERSION = 10;
const MIN_VERSION = 1;

function getBit(x: number, i: number): boolean {
  return ((x >>> i) & 1) !== 0;
}

/** 15-bit format information (level M) for a mask, BCH-encoded (spec §8.9). */
export function formatInfoBits(mask: number): number {
  const data = (0 << 3) | mask; // level M indicator = 00
  let rem = data;
  for (let i = 0; i < 10; i += 1) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
  return ((data << 10) | rem) ^ 0x5412;
}

/** 18-bit version information (versions ≥ 7), BCH-encoded (spec §8.10). */
export function versionInfoBits(version: number): number {
  let rem = version;
  for (let i = 0; i < 12; i += 1) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
  return (version << 12) | rem;
}

// ── Reed–Solomon over GF(2^8) with the QR primitive polynomial ───────

function rsMultiply(x: number, y: number): number {
  let z = 0;
  for (let i = 7; i >= 0; i -= 1) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z & 0xff;
}

function rsDivisor(degree: number): number[] {
  const result = new Array<number>(degree).fill(0);
  result[degree - 1] = 1;
  let root = 1;
  for (let i = 0; i < degree; i += 1) {
    for (let j = 0; j < result.length; j += 1) {
      result[j] = rsMultiply(result[j]!, root);
      if (j + 1 < result.length) result[j] = result[j]! ^ result[j + 1]!;
    }
    root = rsMultiply(root, 0x02);
  }
  return result;
}

function rsRemainder(data: number[], divisor: number[]): number[] {
  const result = new Array<number>(divisor.length).fill(0);
  for (const b of data) {
    const factor = b ^ result.shift()!;
    result.push(0);
    for (let i = 0; i < result.length; i += 1) {
      result[i] = result[i]! ^ rsMultiply(divisor[i]!, factor);
    }
  }
  return result;
}

// ── Raw data-module count per version (spec formula) ─────────────────

function numRawDataModules(ver: number): number {
  let result = (16 * ver + 128) * ver + 64;
  if (ver >= 2) {
    const numAlign = Math.floor(ver / 7) + 2;
    result -= (25 * numAlign - 10) * numAlign - 55;
    if (ver >= 7) result -= 36;
  }
  return result;
}

function dataCodewordsForVersion(ver: number): number {
  const raw = Math.floor(numRawDataModules(ver) / 8);
  return raw - ECC_CODEWORDS_PER_BLOCK[ver]! * NUM_BLOCKS[ver]!;
}

// ── Bit buffer ───────────────────────────────────────────────────────

function appendBits(buf: number[], value: number, len: number): void {
  for (let i = len - 1; i >= 0; i -= 1) buf.push((value >>> i) & 1);
}

/** Encode the UTF-8 bytes of `text` as a byte-mode segment for `version`. */
function encodeData(bytes: number[], version: number): number[] {
  const bits: number[] = [];
  appendBits(bits, 0x4, 4); // byte mode
  appendBits(bits, bytes.length, version < 10 ? 8 : 16);
  for (const b of bytes) appendBits(bits, b, 8);

  const capacityBits = dataCodewordsForVersion(version) * 8;
  // Terminator + pad to a byte boundary.
  appendBits(bits, 0, Math.min(4, capacityBits - bits.length));
  while (bits.length % 8 !== 0) bits.push(0);

  const codewords: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let cw = 0;
    for (let j = 0; j < 8; j += 1) cw = (cw << 1) | bits[i + j]!;
    codewords.push(cw);
  }
  // Pad bytes alternate 0xEC / 0x11.
  for (let pad = 0xec; codewords.length < capacityBits / 8; pad ^= 0xec ^ 0x11) {
    codewords.push(pad);
  }
  return codewords;
}

/** Interleave data blocks with their Reed–Solomon codewords (spec §8.6). */
function addEccAndInterleave(data: number[], version: number): number[] {
  const numBlocks = NUM_BLOCKS[version]!;
  const blockEccLen = ECC_CODEWORDS_PER_BLOCK[version]!;
  const rawCodewords = Math.floor(numRawDataModules(version) / 8);
  const numShort = numBlocks - (rawCodewords % numBlocks);
  const shortLen = Math.floor(rawCodewords / numBlocks); // total codewords, short block
  const shortDataLen = shortLen - blockEccLen;

  // Each block is padded to the same length (shortLen + 1) so the interleave
  // is a simple column walk; short blocks carry a 0 pad at the data tail that
  // is skipped when emitting.
  const blocks: number[][] = [];
  const divisor = rsDivisor(blockEccLen);
  let k = 0;
  for (let i = 0; i < numBlocks; i += 1) {
    const datLen = shortDataLen + (i < numShort ? 0 : 1);
    const dat = data.slice(k, k + datLen);
    k += datLen;
    const ecc = rsRemainder(dat, divisor);
    if (i < numShort) dat.push(0); // pad so every block is shortLen+1 long
    blocks.push([...dat, ...ecc]);
  }

  const result: number[] = [];
  for (let i = 0; i < shortLen + 1; i += 1) {
    for (let b = 0; b < numBlocks; b += 1) {
      // Skip the pad slot of short blocks; long blocks have real data there.
      if (i !== shortDataLen || b >= numShort) result.push(blocks[b]![i]!);
    }
  }
  return result;
}

// ── Matrix construction ──────────────────────────────────────────────

class QrMatrix {
  readonly size: number;
  readonly modules: boolean[][];
  private readonly isFunction: boolean[][];

  constructor(readonly version: number) {
    this.size = version * 4 + 17;
    this.modules = Array.from({ length: this.size }, () =>
      new Array<boolean>(this.size).fill(false),
    );
    this.isFunction = Array.from({ length: this.size }, () =>
      new Array<boolean>(this.size).fill(false),
    );
  }

  private setFunction(x: number, y: number, dark: boolean): void {
    this.modules[y]![x] = dark;
    this.isFunction[y]![x] = true;
  }

  private drawFinder(cx: number, cy: number): void {
    for (let dy = -4; dy <= 4; dy += 1) {
      for (let dx = -4; dx <= 4; dx += 1) {
        const dist = Math.max(Math.abs(dx), Math.abs(dy));
        const x = cx + dx;
        const y = cy + dy;
        if (x >= 0 && x < this.size && y >= 0 && y < this.size) {
          this.setFunction(x, y, dist !== 2 && dist !== 4);
        }
      }
    }
  }

  private drawAlignment(cx: number, cy: number): void {
    for (let dy = -2; dy <= 2; dy += 1) {
      for (let dx = -2; dx <= 2; dx += 1) {
        this.setFunction(cx + dx, cy + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
      }
    }
  }

  private alignmentPositions(): number[] {
    if (this.version === 1) return [];
    const numAlign = Math.floor(this.version / 7) + 2;
    const step = Math.ceil((this.version * 4 + 4) / (numAlign * 2 - 2)) * 2;
    const result = [6];
    for (let pos = this.size - 7; result.length < numAlign; pos -= step) {
      result.splice(1, 0, pos);
    }
    return result;
  }

  drawFunctionPatterns(): void {
    // Timing patterns.
    for (let i = 0; i < this.size; i += 1) {
      this.setFunction(6, i, i % 2 === 0);
      this.setFunction(i, 6, i % 2 === 0);
    }
    // Finder patterns (with their separators, drawn light by the 4-dist ring).
    this.drawFinder(3, 3);
    this.drawFinder(this.size - 4, 3);
    this.drawFinder(3, this.size - 4);
    // Alignment patterns, skipping the three that collide with finders.
    const pos = this.alignmentPositions();
    const n = pos.length;
    for (let i = 0; i < n; i += 1) {
      for (let j = 0; j < n; j += 1) {
        if ((i === 0 && j === 0) || (i === 0 && j === n - 1) || (i === n - 1 && j === 0)) {
          continue;
        }
        this.drawAlignment(pos[i]!, pos[j]!);
      }
    }
    // Reserve the format/version areas (filled after masking).
    this.drawFormatBits(0);
    this.drawVersion();
  }

  private drawFormatBits(mask: number): void {
    const bits = formatInfoBits(mask);

    for (let i = 0; i <= 5; i += 1) this.setFunction(8, i, getBit(bits, i));
    this.setFunction(8, 7, getBit(bits, 6));
    this.setFunction(8, 8, getBit(bits, 7));
    this.setFunction(7, 8, getBit(bits, 8));
    for (let i = 9; i < 15; i += 1) this.setFunction(14 - i, 8, getBit(bits, i));

    for (let i = 0; i < 8; i += 1) this.setFunction(this.size - 1 - i, 8, getBit(bits, i));
    for (let i = 8; i < 15; i += 1) this.setFunction(8, this.size - 15 + i, getBit(bits, i));
    this.setFunction(8, this.size - 8, true); // always dark
  }

  private drawVersion(): void {
    if (this.version < 7) return;
    const bits = versionInfoBits(this.version);
    for (let i = 0; i < 18; i += 1) {
      const color = getBit(bits, i);
      const a = this.size - 11 + (i % 3);
      const b = Math.floor(i / 3);
      this.setFunction(a, b, color);
      this.setFunction(b, a, color);
    }
  }

  drawCodewords(codewords: number[]): void {
    let i = 0; // bit index
    for (let right = this.size - 1; right >= 1; right -= 2) {
      if (right === 6) right = 5; // step over the vertical timing column
      for (let vert = 0; vert < this.size; vert += 1) {
        for (let j = 0; j < 2; j += 1) {
          const x = right - j;
          const upward = ((right + 1) & 2) === 0;
          const y = upward ? this.size - 1 - vert : vert;
          if (!this.isFunction[y]![x] && i < codewords.length * 8) {
            this.modules[y]![x] = getBit(codewords[i >>> 3]!, 7 - (i & 7));
            i += 1;
          }
        }
      }
    }
  }

  applyMask(mask: number): void {
    for (let y = 0; y < this.size; y += 1) {
      for (let x = 0; x < this.size; x += 1) {
        if (this.isFunction[y]![x]) continue;
        let invert = false;
        switch (mask) {
          case 0: invert = (x + y) % 2 === 0; break;
          case 1: invert = y % 2 === 0; break;
          case 2: invert = x % 3 === 0; break;
          case 3: invert = (x + y) % 3 === 0; break;
          case 4: invert = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0; break;
          case 5: invert = ((x * y) % 2) + ((x * y) % 3) === 0; break;
          case 6: invert = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0; break;
          case 7: invert = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0; break;
          default: break;
        }
        if (invert) this.modules[y]![x] = !this.modules[y]![x];
      }
    }
  }

  finalize(codewords: number[]): void {
    this.drawFunctionPatterns();
    this.drawCodewords(codewords);
    // Pick the mask with the lowest penalty (spec §8.8).
    let bestMask = 0;
    let bestPenalty = Infinity;
    for (let mask = 0; mask < 8; mask += 1) {
      this.applyMask(mask);
      this.drawFormatBits(mask);
      const penalty = this.penaltyScore();
      if (penalty < bestPenalty) {
        bestPenalty = penalty;
        bestMask = mask;
      }
      this.applyMask(mask); // undo
    }
    this.applyMask(bestMask);
    this.drawFormatBits(bestMask);
  }

  private penaltyScore(): number {
    const size = this.size;
    const mods = this.modules;
    let penalty = 0;

    // Rule 1: runs of ≥5 same-color modules in each row and column.
    const runPenalty = (get: (i: number) => boolean): number => {
      let sub = 0;
      let color = get(0);
      let run = 1;
      for (let i = 1; i < size; i += 1) {
        if (get(i) === color) {
          run += 1;
          if (run === 5) sub += 3;
          else if (run > 5) sub += 1;
        } else {
          color = get(i);
          run = 1;
        }
      }
      return sub;
    };
    for (let y = 0; y < size; y += 1) penalty += runPenalty((x) => mods[y]![x]!);
    for (let x = 0; x < size; x += 1) penalty += runPenalty((y) => mods[y]![x]!);

    // Rule 2: 2×2 blocks of a single color.
    for (let y = 0; y < size - 1; y += 1) {
      for (let x = 0; x < size - 1; x += 1) {
        const c = mods[y]![x];
        if (c === mods[y]![x + 1] && c === mods[y + 1]![x] && c === mods[y + 1]![x + 1]) {
          penalty += 3;
        }
      }
    }

    // Rule 3: the finder-like 1:1:3:1:1 pattern with a 4-module light margin.
    const P1 = [true, false, true, true, true, false, true, false, false, false, false];
    const P2 = [false, false, false, false, true, false, true, true, true, false, true];
    const matches = (get: (i: number) => boolean, start: number, pat: boolean[]): boolean => {
      for (let i = 0; i < 11; i += 1) if (get(start + i) !== pat[i]) return false;
      return true;
    };
    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x <= size - 11; x += 1) {
        const get = (i: number) => mods[y]![i]!;
        if (matches(get, x, P1) || matches(get, x, P2)) penalty += 40;
      }
    }
    for (let x = 0; x < size; x += 1) {
      for (let y = 0; y <= size - 11; y += 1) {
        const get = (i: number) => mods[i]![x]!;
        if (matches(get, y, P1) || matches(get, y, P2)) penalty += 40;
      }
    }

    // Rule 4: deviation of dark-module share from 50%.
    let dark = 0;
    for (let y = 0; y < size; y += 1) for (let x = 0; x < size; x += 1) if (mods[y]![x]) dark += 1;
    const total = size * size;
    const k = Math.floor((Math.abs(dark * 20 - total * 10) + total - 1) / total) - 1;
    penalty += k * 10;
    return penalty;
  }
}

/** Choose the smallest version 1–10 whose level-M capacity fits `bytes`. */
function fitVersion(byteLen: number): number {
  for (let v = MIN_VERSION; v <= MAX_VERSION; v += 1) {
    const cap = dataCodewordsForVersion(v);
    const header = 1 + (v < 10 ? 1 : 2); // mode nibble+count ≈ header bytes
    if (byteLen + header <= cap) return v;
  }
  throw new Error('Data too long for a QR code');
}

/**
 * Build the QR module matrix for `text` (true = dark). No quiet zone — the
 * renderer adds margin. Throws only if the text exceeds v10-M capacity, which
 * an otpauth URL never does.
 */
export function generateQrMatrix(text: string): boolean[][] {
  const bytes = Array.from(new TextEncoder().encode(text));
  const version = fitVersion(bytes.length);
  const codewords = addEccAndInterleave(encodeData(bytes, version), version);
  const qr = new QrMatrix(version);
  qr.finalize(codewords);
  return qr.modules;
}
